import { useMutation, useSuspenseQuery } from '@apollo/client'
import {
  FetchPoliciesDocument,
  type PolicyUserRoleFieldsFragment,
  ReplaceCustomRolePoliciesDocument,
  UpdatePolicyUserRoleDocument,
} from '@salescore/client-api'
import { getOrganizationIdFromPath, Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { SuspenseWithLoading } from '@salescore/client-common'
// eslint-disable-next-line sonarjs/deprecation
import type { useModal } from '@salescore/frontend-common'
import { message, Modal } from 'antd'
import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PolicyForm } from './PolicyForm'
import {
  buildActionsWithPermission,
  buildPolicyFormValue,
  PolicyFormSchema,
  type PolicyFormValue,
} from './policyFormValue'

export const PolicyEditModal = ({
  modal,
}: {
  // eslint-disable-next-line sonarjs/deprecation
  modal: ReturnType<typeof useModal<{ policyUserRole: PolicyUserRoleFieldsFragment }>>
}): ReactElement => {
  const { t } = useTranslation()

  return (
    <Modal
      open={modal.isModalVisible}
      onCancel={modal.hideModal}
      width={'60%'}
      cancelText={t(`閉じる`)}
      okButtonProps={{ style: { display: 'none' } }}
      destroyOnHidden
    >
      <SuspenseWithLoading>
        <>
          {modal.content !== undefined && (
            <Body
              policyUserRole={modal.content.policyUserRole}
              onAfterFinish={() => {
                modal.hideModal()
              }}
            />
          )}
        </>
      </SuspenseWithLoading>
    </Modal>
  )
}

const Body = ({
  policyUserRole,
  onAfterFinish,
}: {
  policyUserRole: PolicyUserRoleFieldsFragment
  onAfterFinish: () => void
}): ReactElement => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [updatePolicyUserRoleMutation] = useMutation(UpdatePolicyUserRoleDocument)
  const [replaceCustomRolePoliciesMutation] = useMutation(ReplaceCustomRolePoliciesDocument)
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: BE で principal で絞り込めるようにする。実現したら organizationId 単位で組織の全 policy を
  // 引く必要がなくなるので、この query の粒度と下の受容判断を見直す。
  //
  // 保存は principal の全行を作り直す全置換なので、初期値が古いとフォームに出ていない他者の変更を巻き戻す。
  // network-only でキャッシュを読ませず、応答が返るまで suspend させて初期値を最新に固定する
  // (キャッシュにヒットする fetchPolicy だと suspend せずマウントし、後から届く応答は
  // mount 時のみ有効な initialValues には効かない)。
  //
  // 承知のうえで受容している残存リスク (#9712 の敵対レビュー指摘):
  // (a) 楽観排他が無いため、取得から保存までの間に他の管理者が保存すると巻き戻る。window が
  //     「キャッシュの古さ」から「取得〜保存」に狭まるだけで消えない。修正には revision による
  //     排他制御が必要で、本 MR のスコープを超える。
  // (b) suspend するのは query reference が新規に作られる場合だけ。useSuspenseQuery の cache key は
  //     [query, variables, ...queryKey] で fetchPolicy を含まず、既存の fulfilled な reference が
  //     生きていると applyOptions は silentSetOptions するだけで再 fetch しない。modal を閉じると
  //     reference は dispose されるため現状は発火しないが、同じ query を別箇所で useSuspenseQuery
  //     すると破綻する。その時は queryKey で reference を分離する。
  const { data } = useSuspenseQuery(FetchPoliciesDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
    },
    fetchPolicy: 'network-only',
  })

  const onFinish = async (value: PolicyFormValue): Promise<void> => {
    const validated = PolicyFormSchema.safeParse(value)
    if (!validated.success) {
      void message.warning(t(`入力に誤りがあります`))
      return
    }

    setLoading(true)
    try {
      onAfterFinish()
      const result = await updatePolicyUserRoleMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          policyUserRole: {
            id: policyUserRole.id,
            label: validated.data.label,
            description: validated.data.description ?? '',
          },
        },
        refetchQueries: ['fetchPolicyUserRoles'],
      })

      const actionsWithPermission = buildActionsWithPermission(validated.data.actionsWithPermission)

      if (result.data?.updatePolicyUserRole.name === undefined) {
        throw new Error(`権限セットを更新できませんでした`)
      } else {
        const customRolePolicies = await replaceCustomRolePoliciesMutation({
          variables: {
            organizationId: getOrganizationIdFromPath(),
            customRolePolicies: {
              userRole: result.data.updatePolicyUserRole.name,
              actionsWithPermission,
            },
          },
          refetchQueries: ['fetchPolicies'],
        })
        Posthog.track(POSTHOG_EVENTS.save_custom_role, {
          organizationId: getOrganizationIdFromPath(),
          config: customRolePolicies.data?.replaceCustomRolePolicies,
        })
      }
      void message.success(t(`権限セットを更新しました`))
    } catch {
      void message.error(t(`エラーが発生しました`))
    } finally {
      setLoading(false)
    }
  }

  return (
    <PolicyForm
      onFinish={onFinish}
      loading={loading}
      initialValue={buildPolicyFormValue(policyUserRole, data.policies)}
      isEditing
    />
  )
}
